/**
 * resolveToolIntent.js — Tool candidate ranking from operational context (FASE 19.5)
 *
 * Combines: operational mode + active signals + current module +
 *           message intent + entity context → ranked tool candidates.
 *
 * Does NOT execute anything. Returns a scored list for the UI layer
 * or for the orchestrator to act on after user confirmation.
 */

import { getToolById }               from "./toolRegistry";
import { POLICIES, resolvePolicy }   from "./executionPolicy";
import { SIGNALS }                   from "../kevia-signals";

/* ─── Signal → tool affinity map ─────────────────────────────────────── */

const SIGNAL_TOOL_MAP = {
  [SIGNALS.FOLLOW_UP_RISK]:          ["open_visits", "open_family", "create_visit"],
  [SIGNALS.INACTIVE_FAMILY_PATTERN]: ["open_family", "open_visits"],
  [SIGNALS.HIGH_VISIT_ACTIVITY]:     ["open_visits", "create_visit"],
  [SIGNALS.AUDIT_INVESTIGATION]:     ["open_audit"],
  [SIGNALS.PRAYER_FOCUS]:            ["open_prayer"],
  [SIGNALS.MEMBER_RECOVERY_FLOW]:    ["open_visits", "create_visit", "open_prayer"],
  [SIGNALS.ADMIN_ATTENTION]:         ["open_audit"],
  [SIGNALS.LEADERSHIP_REVIEW]:       [],
};

/* ─── Mode → default tool affinity ───────────────────────────────────── */

const MODE_TOOL_MAP = {
  PASTORAL_CARE:       ["open_family", "open_visits"],
  FOLLOW_UP:           ["open_visits", "create_visit"],
  ADMIN_REVIEW:        ["open_audit"],
  DISTRICT_OVERSIGHT:  ["open_family", "open_visits"],
  MESSAGE_PREPARATION: [],
  ANALYTICS_REVIEW:    ["open_audit"],
};

/* ─── Passive hint strings ────────────────────────────────────────────── */

const TOOL_HINTS = {
  open_visits:  "Puedes revisar las visitas pastorales recientes.",
  open_family:  "Hay familias que podrían necesitar seguimiento.",
  create_visit: "Puedes registrar una visita si es necesario.",
  open_audit:   "El registro de auditoría está disponible.",
  open_prayer:  "Puedes revisar las peticiones de oración activas.",
};

/* ─── Public API ──────────────────────────────────────────────────────── */

/**
 * Resolves and ranks tool candidates from runtime context.
 * Returns max 3 candidates, excluding BLOCKED tools.
 *
 * @param {{
 *   operationalMode?: string|null,
 *   signals?:        Array<{ id, score }>,
 *   currentModule?:  string|null,
 *   messageIntent?:  string|null,
 *   entityContext?:  object|null,
 *   userRole?:       string,
 * }} opts
 * @returns {Array<{ tool, score: number, reason: string, policy: string }>}
 */
export function resolveToolIntent({
  operationalMode = null,
  signals         = [],
  currentModule   = null,
  messageIntent   = null,
  entityContext   = null,
  userRole        = "*",
} = {}) {
  const scores = {};

  // 1. Score from signals (highest weight — direct behavioral evidence)
  for (const signal of signals.slice(0, 4)) {
    const toolIds = SIGNAL_TOOL_MAP[signal.id] ?? [];
    toolIds.forEach((id, rank) => {
      scores[id] = (scores[id] ?? 0) + signal.score * (1 - rank * 0.2);
    });
  }

  // 2. Score from operational mode
  const modeTools = MODE_TOOL_MAP[operationalMode] ?? [];
  modeTools.forEach((id, rank) => {
    scores[id] = (scores[id] ?? 0) + (0.45 - rank * 0.1);
  });

  // 3. Entity context boost
  if (entityContext?.type === "family") scores["open_family"]  = (scores["open_family"]  ?? 0) + 0.35;
  if (entityContext?.type === "visit")  scores["open_visits"]  = (scores["open_visits"]  ?? 0) + 0.35;
  if (entityContext?.type === "prayer") scores["open_prayer"]  = (scores["open_prayer"]  ?? 0) + 0.35;

  return Object.entries(scores)
    .map(([id, rawScore]) => {
      const tool = getToolById(id);
      if (!tool) return null;
      const policy = resolvePolicy(tool, userRole);
      if (policy === POLICIES.BLOCKED) return null;
      return {
        tool,
        score:  Math.min(1, rawScore),
        reason: operationalMode
          ? `modo: ${operationalMode.toLowerCase().replace(/_/g, " ")}`
          : "contexto de sesión",
        policy,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
}

/**
 * Returns a single calm, pastoral hint string from the top tool candidate.
 * Returns null if confidence is too low or no candidates exist.
 *
 * @param {ReturnType<typeof resolveToolIntent>} candidates
 * @returns {string|null}
 */
export function getToolHint(candidates) {
  if (!candidates?.length) return null;
  const top = candidates[0];
  if (top.score < 0.38) return null;
  return TOOL_HINTS[top.tool.id] ?? null;
}
